import { UUIDs } from "@/types/uuids";
import dayjs from "dayjs";
import type { PatientData } from "../../patients-data";

export const timePoints = [
	"Baseline",
	"3 month",
	"6 month",
	"9 month",
	"1 year",
	"2 year",
	"3 year",
] as const;

export type TimePoint = (typeof timePoints)[number];

export interface OxfordDataPoint {
	name: string;
	date: string;
	timing: string;
	"Oxford Score": number;
}

export interface OxfordChartConfig {
	[key: string]: {
		label: string;
		color: string;
	};
}

export const oxfordChartConfig: OxfordChartConfig = {
	"Oxford Score": {
		label: "Oxford Score",
		color: "hsl(var(--chart-3))",
	},
};

export function round2dp(value: number): string {
	return value.toFixed(2);
}

export function formatPercentage(value: number, max: number): string {
	return `${((value / max) * 100).toFixed(2)}%`;
}

export function processOxfordData(patients: PatientData[]): OxfordDataPoint[] {
	return timePoints.map((timePoint) => {
		// Calculate average scores
		const scores = patients
			.map((patient) => {
				const oxford = patient.oxford.find((o) => o.label === timePoint);
				return oxford ? oxford.score : 0;
			})
			.filter((score) => score > 0);

		// Calculate average
		const avgScore =
			scores.length > 0
				? Number(
						(
							scores.reduce((sum, score) => sum + score, 0) / scores.length
						).toFixed(2),
					)
				: 0;

		// Create date for x-axis (using current date as reference)
		const date = new Date();
		// Adjust date based on time point
		if (timePoint === "Baseline") {
			date.setMonth(date.getMonth() - 12); // 1 year ago
		} else if (timePoint === "3 month") {
			date.setMonth(date.getMonth() - 9);
		} else if (timePoint === "6 month") {
			date.setMonth(date.getMonth() - 6);
		} else if (timePoint === "9 month") {
			date.setMonth(date.getMonth() - 3);
		} else if (timePoint === "1 year") {
			// Current date
		} else if (timePoint === "2 year") {
			date.setMonth(date.getMonth() + 12);
		} else if (timePoint === "3 year") {
			date.setMonth(date.getMonth() + 24);
		}

		return {
			name: timePoint,
			date: date.toISOString(),
			timing: timePoint,
			"Oxford Score": avgScore,
		};
	});
}

export function getOxfordAverage(data: OxfordDataPoint[]): OxfordDataPoint {
	const avgScore =
		data.reduce((sum, d) => sum + d["Oxford Score"], 0) / data.length;

	return {
		name: "Average",
		date: "",
		timing: "Average",
		"Oxford Score": Number(avgScore.toFixed(2)),
	};
}
